// DownloadModal - Download management interface for workspace exports
// Implements US-6.3: MinIO Integration and Download Page

import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Image, Table, Package } from 'lucide-react';
import styles from './DownloadModal.module.css';
import { getWorkspaceExports, ExportFile } from '../services/exportService';

interface DownloadModalProps {
  workspaceId: string;
  workspaceName: string;
  onClose: () => void;
}

/**
 * RENDER download modal with list of available exports
 * - Fetches export files from MinIO via API
 * - Displays file metadata (format, size, date)
 * - Provides download buttons with presigned URLs
 */
const DownloadModal: React.FC<DownloadModalProps> = ({ workspaceId, workspaceName, onClose }) => {
  const [exports, setExports] = useState<ExportFile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * FETCH export files from API
   */
  const fetchExports = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getWorkspaceExports(workspaceId);

      if (response.success) {
        setExports(response.exports);
      } else {
        throw new Error(response.message || 'Failed to fetch exports');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  /**
   * FETCH exports on component mount
   */
  useEffect(() => {
    fetchExports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  /**
   * HANDLE download click
   * - Opens presigned URL in new tab
   */
  const handleDownload = (downloadUrl: string) => {
    window.open(downloadUrl, '_blank');
  };

  /**
   * GET icon component based on format
   */
  const getFormatIcon = (format: string) => {
    switch (format.toLowerCase()) {
      case 'pdf':
        return <FileText size={24} strokeWidth={1.5} />;
      case 'png':
        return <Image size={24} strokeWidth={1.5} />;
      case 'csv':
        return <Table size={24} strokeWidth={1.5} />;
      case 'json':
        return <Package size={24} strokeWidth={1.5} />;
      default:
        return <FileText size={24} strokeWidth={1.5} />;
    }
  };

  /**
   * FORMAT file size to human-readable format
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * FORMAT date to readable format
   */
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * GET format display name
   */
  const getFormatDisplayName = (format: string): string => {
    switch (format.toLowerCase()) {
      case 'pdf':
        return 'PDF Report';
      case 'png':
        return 'PNG Visualization';
      case 'csv':
        return 'CSV Data';
      case 'json':
        return 'JSON Export';
      default:
        return format.toUpperCase();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* MODAL header */}
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h2 className={styles.title}>Download Exports</h2>
            <p className={styles.subtitle}>{workspaceName}</p>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        {/* MODAL content */}
        <div className={styles.content}>
          {/* LOADING state */}
          {loading && (
            <div className={styles.loadingState}>
              <p>Loading exports...</p>
            </div>
          )}

          {/* ERROR state */}
          {error && !loading && (
            <div className={styles.errorState}>
              <p className={styles.errorMessage}>{error}</p>
              <button className={styles.retryButton} onClick={fetchExports}>
                Retry
              </button>
            </div>
          )}

          {/* EXPORTS list */}
          {!loading && !error && exports.length > 0 && (
            <div className={styles.exportsList}>
              {exports.map((exportFile, index) => (
                <div key={index} className={styles.exportItem}>
                  {/* FORMAT icon */}
                  <div className={styles.iconSection}>
                    {getFormatIcon(exportFile.format)}
                  </div>

                  {/* FILE info */}
                  <div className={styles.infoSection}>
                    <h3 className={styles.formatName}>
                      {getFormatDisplayName(exportFile.format)}
                    </h3>
                    <div className={styles.metadata}>
                      <span className={styles.metadataItem}>
                        {formatFileSize(exportFile.size)}
                      </span>
                      <span className={styles.metadataSeparator}>•</span>
                      <span className={styles.metadataItem}>
                        {formatDate(exportFile.lastModified)}
                      </span>
                    </div>
                  </div>

                  {/* DOWNLOAD button */}
                  <div className={styles.actionSection}>
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleDownload(exportFile.downloadUrl)}
                      aria-label={`Download ${exportFile.format} export`}
                    >
                      <Download size={20} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* EMPTY state */}
          {!loading && !error && exports.length === 0 && (
            <div className={styles.emptyState}>
              <p>No exports available for this workspace.</p>
            </div>
          )}
        </div>

        {/* MODAL footer */}
        <div className={styles.footer}>
          <p className={styles.footerNote}>
            Download links expire after 24 hours. Click the export badge again to refresh.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
